<template>
  <div class="device-commands">
    <loading
      :active.sync="isLoading"
      color="#9de35c"
      loader="bars"
      :width="64"
      :height="64"
      :is-full-page="true"
    />

    <v-alert
      v-if="!canAccess"
      dense
      text
      type="error"
      class="ma-4"
    >
      Device command tracking is available to Admin and SuperUser accounts only.
    </v-alert>

    <template v-else>
      <v-row class="mx-0 mb-2" align="center">
        <v-col cols="auto" class="py-1 py-sm-2">
          <v-btn text @click="goBack">
            <v-icon left>mdi-arrow-left</v-icon> Back to Devices
          </v-btn>
        </v-col>
        <v-spacer />
        <v-col cols="auto" class="py-1 py-sm-2">
          <v-switch
            v-model="autoRefresh"
            dense
            hide-details
            class="mt-0 pt-0"
            label="Auto refresh (15s)"
          />
        </v-col>
        <v-col cols="auto" class="py-1 py-sm-2 text-right">
          <app-btn @click="refreshAll()">
            <v-icon class="mr-1">mdi-refresh</v-icon> Refresh
          </app-btn>
        </v-col>
      </v-row>

      <!-- Device lookup -->
      <material-card
        icon="mdi-cellphone-link"
        icon-small
        color="primary"
        title="Device"
      >
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" sm="4" md="3" class="py-2">
              <v-text-field
                v-model="lookup.deviceId"
                label="Device Id"
                hide-details
                clearable
                @keyup.enter="applyLookup"
              />
            </v-col>
            <v-col cols="12" sm="5" md="4" class="py-2">
              <v-text-field
                v-model="lookup.macAddress"
                label="MAC Address"
                hide-details
                clearable
                placeholder="02:9F:17:AC:52:EE"
                @keyup.enter="applyLookup"
              />
            </v-col>
            <v-col cols="12" sm="3" md="2" class="py-2">
              <app-btn @click="applyLookup">
                <v-icon class="mr-1">mdi-magnify</v-icon> Track
              </app-btn>
            </v-col>
          </v-row>

          <v-divider v-if="device.deviceId" class="my-3" />

          <v-row v-if="device.deviceId" dense align="center">
            <v-col cols="12" md="7">
              <div class="device-summary">
                <span class="device-summary__name">{{ device.name || 'Unnamed device' }}</span>
                <v-chip
                  x-small
                  class="ml-2"
                  :color="device.online ? 'success' : 'grey'"
                  text-color="white"
                >
                  {{ device.online ? 'Online' : 'Offline' }}
                </v-chip>
              </div>
              <div class="device-summary__meta">
                <span><strong>Id:</strong> {{ device.deviceId }}</span>
                <span v-if="device.macAddress"><strong>MAC:</strong> <code>{{ device.macAddress }}</code></span>
                <span v-if="device.companyName"><strong>Company:</strong> {{ device.companyName }}</span>
              </div>
            </v-col>
            <v-col cols="12" md="5" class="text-md-right">
              <app-btn
                color="error"
                :disabled="!summary.pending"
                @click="openCancelAllDialog"
              >
                <v-icon class="mr-1">mdi-cancel</v-icon>
                Cancel all pending ({{ summary.pending || 0 }})
              </app-btn>
            </v-col>
          </v-row>

          <template v-if="device.deviceId">
            <v-divider class="my-3" />

            <div class="command-summary">
              <div class="command-summary__stats">
                <div class="stat-item">
                  <span class="stat-value">{{ summary.total || 0 }}</span>
                  <span class="stat-label">Total</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value warning--text">{{ summary.pending || 0 }}</span>
                  <span class="stat-label">Pending</span>
                </div>
                <div class="stat-item">
                  <span class="stat-value success--text">{{ summary.completed || 0 }}</span>
                  <span class="stat-label">Completed</span>
                </div>
              </div>
              <div class="command-summary__chips">
                <v-chip
                  v-for="entry in byStatusList"
                  :key="entry.status"
                  small
                  class="mr-2 mb-1"
                  :color="statusColor(entry.status)"
                  text-color="white"
                >
                  {{ entry.status }}: {{ entry.count }}
                </v-chip>
                <span v-if="!byStatusList.length" class="grey--text">No commands recorded</span>
              </div>
            </div>
          </template>
        </v-card-text>
      </material-card>

      <!-- Commands -->
      <material-card
        v-if="device.deviceId || loading"
        icon="mdi-history"
        icon-small
        color="warning"
        title="SignalR Command Tracking"
        class="mt-4"
      >
        <v-tabs v-model="tab" class="px-4">
          <v-tab>History</v-tab>
          <v-tab>
            Pending
            <v-chip
              v-if="summary.pending"
              x-small
              class="ml-2"
              color="warning"
              text-color="white"
            >
              {{ summary.pending }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-card-text>
          <v-row>
            <v-col v-if="tab === 0" cols="12" sm="6" md="2" class="py-2">
              <v-select
                v-model="filters.state"
                :items="stateOptions"
                label="State"
                hide-details
                @change="reload"
              />
            </v-col>
            <v-col v-if="tab === 0" cols="12" sm="6" md="3" class="py-2">
              <v-select
                v-model="filters.status"
                :items="statusOptions"
                label="Status"
                multiple
                small-chips
                deletable-chips
                clearable
                hide-details
                @change="reload"
              />
            </v-col>
            <v-col cols="12" sm="6" md="2" class="py-2">
              <v-combobox
                v-model="filters.type"
                :items="typeOptions"
                label="Command type"
                clearable
                hide-details
                @change="reload"
              />
            </v-col>
            <v-col v-if="tab === 0" cols="12" sm="6" md="2" class="py-2">
              <v-menu
                v-model="fromMenu"
                :close-on-content-click="false"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filters.from"
                    label="From"
                    readonly
                    clearable
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="clearDate('from')"
                  />
                </template>
                <v-date-picker
                  v-model="filters.from"
                  @input="onFromPicked"
                />
              </v-menu>
            </v-col>
            <v-col v-if="tab === 0" cols="12" sm="6" md="2" class="py-2">
              <v-menu
                v-model="toMenu"
                :close-on-content-click="false"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    :value="filters.to"
                    label="To"
                    readonly
                    clearable
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="clearDate('to')"
                  />
                </template>
                <v-date-picker
                  v-model="filters.to"
                  @input="onToPicked"
                />
              </v-menu>
            </v-col>
            <v-col cols="12" md="1" class="py-2 d-flex align-center">
              <v-btn text small @click="resetFilters">Reset</v-btn>
            </v-col>
          </v-row>

          <v-row v-if="selectedPendingIds.length" dense class="mb-1">
            <v-col cols="12">
              <v-alert dense text type="warning" class="mb-0">
                {{ selectedPendingIds.length }} pending command(s) selected.
                <v-btn
                  small
                  color="error"
                  class="ml-3"
                  @click="openCancelSelectedDialog"
                >
                  Cancel selected
                </v-btn>
              </v-alert>
            </v-col>
          </v-row>

          <v-divider class="mt-1" />

          <v-data-table
            v-model="selected"
            :headers="tableHeaders"
            :items="commands"
            :loading="loading"
            :page="page"
            :items-per-page="pageSize"
            :server-items-length="totalCount"
            @update:page="onPageChange"
            @update:items-per-page="onPageSizeChange"
            item-key="commandId"
            show-select
            :footer-props="{
              showFirstLastPage: true,
              'items-per-page-options': [10, 25, 50, 100],
            }"
            no-data-text="No commands found for this device"
          >
            <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
              <v-simple-checkbox
                v-if="item.isPending"
                :value="isSelected"
                :ripple="false"
                @input="select($event)"
              />
              <v-icon v-else small color="grey lighten-1">mdi-lock</v-icon>
            </template>

            <template v-slot:[`item.commandId`]="{ item }">
              <button
                type="button"
                class="primary--text command-link"
                @click="openDetail(item)"
              >
                #{{ item.commandId }}
              </button>
            </template>

            <template v-slot:[`item.type`]="{ item }">
              <v-chip x-small color="primary" text-color="white">{{ item.type }}</v-chip>
              <div v-if="hasPayload(item)" class="payload">
                <v-btn
                  x-small
                  text
                  color="primary"
                  class="px-1"
                  @click="openPayload(item)"
                >
                  <v-icon x-small class="mr-1">mdi-code-braces</v-icon>
                  View payload
                </v-btn>
              </div>
            </template>

            <template v-slot:[`item.status`]="{ item }">
              <v-chip
                x-small
                :color="statusColor(item.status)"
                text-color="white"
              >
                {{ item.status }}
              </v-chip>
              <div v-if="item.isPending" class="caption warning--text">awaiting device</div>
            </template>

            <template v-slot:[`item.timeline`]="{ item }">
              <div class="timeline">
                <div class="timeline__row">
                  <span class="timeline__k">Queued</span>
                  <span class="timeline__v">{{ formatDate(item.createdAt) }}</span>
                </div>
                <div class="timeline__row">
                  <span class="timeline__k">Dispatched</span>
                  <span :class="['timeline__v', item.dispatchedAt ? '' : 'grey--text']">
                    {{ item.dispatchedAt ? formatDate(item.dispatchedAt) : 'not sent' }}
                  </span>
                </div>
                <div class="timeline__row">
                  <span class="timeline__k">Last try</span>
                  <span :class="['timeline__v', item.lastDeliveryAttemptAt ? '' : 'grey--text']">
                    {{ item.lastDeliveryAttemptAt ? formatDate(item.lastDeliveryAttemptAt) : '-' }}
                  </span>
                </div>
                <div class="timeline__row">
                  <span class="timeline__k">Ack</span>
                  <span :class="['timeline__v', item.acknowledgedAt ? 'success--text' : 'warning--text']">
                    {{ item.acknowledgedAt ? formatDate(item.acknowledgedAt) : 'pending' }}
                  </span>
                </div>
                <div class="timeline__row">
                  <span class="timeline__k">Expires</span>
                  <span :class="['timeline__v', isExpired(item) ? 'error--text' : 'grey--text']">
                    {{ item.expiresAt ? formatDate(item.expiresAt) : 'never' }}
                  </span>
                </div>
              </div>
            </template>

            <template v-slot:[`item.deliveryAttempts`]="{ item }">
              <v-chip
                x-small
                :color="attemptsColor(item.deliveryAttempts)"
                text-color="white"
              >
                {{ item.deliveryAttempts || 0 }}
              </v-chip>
            </template>

            <template v-slot:[`item.sentBy`]="{ item }">
              <span :class="item.sentBy ? '' : 'grey--text'">{{ item.sentBy || 'system' }}</span>
              <div v-if="item.deviceGroupId" class="caption grey--text">
                group #{{ item.deviceGroupId }}
              </div>
            </template>

            <template v-slot:[`item.lastDeliveryError`]="{ item }">
              <v-tooltip v-if="item.lastDeliveryError" bottom max-width="360">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="error" v-bind="attrs" v-on="on">
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>{{ item.lastDeliveryError }}</span>
              </v-tooltip>
              <span v-else class="grey--text">-</span>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon small title="View detail" @click="openDetail(item)">
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn>
              <v-btn
                v-if="item.isPending"
                icon
                small
                color="error"
                title="Cancel this pending command"
                @click="openCancelOneDialog(item)"
              >
                <v-icon small>mdi-cancel</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </material-card>
    </template>

    <!-- Cancel dialog -->
    <v-dialog v-model="cancelDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="headline">{{ cancelTitle }}</v-card-title>
        <v-card-text>
          <p class="mb-2">{{ cancelMessage }}</p>
          <v-text-field
            v-model="cancelReason"
            label="Reason (recorded against the command)"
            placeholder="Superseded by unlock"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="cancelling" @click="cancelDialog = false">Close</v-btn>
          <v-btn
            color="error"
            :loading="cancelling"
            @click="confirmCancel"
          >
            Cancel command(s)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Detail dialog -->
    <v-dialog v-model="detailDialog" max-width="680" scrollable>
      <v-card v-if="detail">
        <v-card-title class="headline">
          Command #{{ detail.commandId }}
          <v-chip
            x-small
            class="ml-3"
            :color="statusColor(detail.status)"
            text-color="white"
          >
            {{ detail.status }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <tbody>
              <tr v-for="row in detailRows" :key="row.label">
                <td class="detail-key">{{ row.label }}</td>
                <td>{{ row.value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="hasPayload(detail)"
            text
            color="primary"
            @click="openPayload(detail)"
          >
            <v-icon left small>mdi-code-braces</v-icon> View payload
          </v-btn>
          <v-btn
            v-if="detail.isPending"
            color="error"
            text
            @click="openCancelOneDialog(detail)"
          >
            <v-icon left small>mdi-cancel</v-icon> Cancel command
          </v-btn>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payload dialog -->
    <v-dialog v-model="payloadDialog" max-width="820" scrollable>
      <v-card>
        <v-card-title class="headline">
          Payload
          <v-chip
            v-if="payloadCommand"
            x-small
            class="ml-3"
            color="primary"
            text-color="white"
          >
            {{ payloadCommand.type }}
          </v-chip>
          <span v-if="payloadCommand" class="ml-2 grey--text">#{{ payloadCommand.commandId }}</span>
        </v-card-title>
        <v-card-text>
          <div
            v-for="section in payloadSections"
            :key="section.label"
            class="mb-4"
          >
            <div class="payload-dialog__label">{{ section.label }}</div>
            <pre class="payload-dialog__body">{{ section.text }}</pre>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="payloadDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import Loading from 'vue-loading-overlay'
  import 'vue-loading-overlay/dist/vue-loading.css'
  import MaterialCard from '@/components/MaterialCard'
  import DeviceCommandsDataService from '@/services/DeviceCommandsDataService'

  const DEFAULT_STATUSES = [
    'Queued',
    'Dispatched',
    'Succeeded',
    'Failed',
    'Expired',
    'Cancelled',
  ]

  export default {
    name: 'DeviceCommands',

    components: {
      Loading,
      MaterialCard,
    },

    data () {
      return {
        isLoading: false,
        loading: false,
        cancelling: false,
        autoRefresh: false,
        refreshTimer: null,

        tab: 0,
        lookup: {
          deviceId: '',
          macAddress: '',
        },
        target: {
          deviceId: null,
          macAddress: null,
        },

        device: {},
        summary: {},
        commands: [],
        totalCount: 0,

        page: 1,
        pageSize: 25,

        filters: {
          state: 'all',
          status: [],
          type: null,
          from: null,
          to: null,
        },
        fromMenu: false,
        toMenu: false,

        statusVocabulary: [],
        stateVocabulary: [],
        typeVocabulary: [],

        selected: [],

        cancelDialog: false,
        cancelReason: '',
        cancelMode: 'one',
        cancelTargets: [],

        detailDialog: false,
        detail: null,

        payloadDialog: false,
        payloadCommand: null,
      }
    },

    computed: {
      canAccess () {
        return this.$store.getters['authUser/isAdminOrSuperUser']
      },

      stateOptions () {
        if (this.stateVocabulary.length) {
          return this.stateVocabulary
        }
        return [
          { text: 'All', value: 'all' },
          { text: 'Pending', value: 'pending' },
          { text: 'Completed', value: 'completed' },
        ]
      },

      statusOptions () {
        return this.statusVocabulary.length ? this.statusVocabulary : DEFAULT_STATUSES
      },

      typeOptions () {
        const fromRows = this.commands.map(command => command.type).filter(Boolean)
        return [...new Set([...this.typeVocabulary, ...fromRows])]
      },

      byStatusList () {
        const byStatus = this.summary.byStatus || {}
        return Object.keys(byStatus)
          .filter(status => byStatus[status])
          .map(status => ({ status, count: byStatus[status] }))
      },

      selectedPendingIds () {
        return this.selected
          .filter(command => command.isPending)
          .map(command => command.commandId)
      },

      tableHeaders () {
        return [
          { text: 'Id', value: 'commandId', sortable: false, width: '80px' },
          { text: 'Type', value: 'type', sortable: false },
          { text: 'Status', value: 'status', sortable: false },
          { text: 'Timeline', value: 'timeline', sortable: false, width: '260px' },
          { text: 'Attempts', value: 'deliveryAttempts', sortable: false, align: 'center' },
          { text: 'Sent by', value: 'sentBy', sortable: false },
          { text: 'Error', value: 'lastDeliveryError', sortable: false, align: 'center' },
          { text: '', value: 'actions', sortable: false, align: 'end', width: '96px' },
        ]
      },

      cancelTitle () {
        if (this.cancelMode === 'all') {
          return 'Cancel all pending commands'
        }
        return this.cancelTargets.length > 1 ? 'Cancel selected commands' : 'Cancel pending command'
      },

      cancelMessage () {
        if (this.cancelMode === 'all') {
          const type = this.filters.type ? ` of type "${this.filters.type}"` : ''
          return `Every command still awaiting this device${type} will be cancelled. The device stops receiving them on its next poll.`
        }
        if (this.cancelTargets.length > 1) {
          return `Commands ${this.cancelTargets.map(id => '#' + id).join(', ')} will be cancelled.`
        }
        return `Command #${this.cancelTargets[0]} will be cancelled and the device will stop retrying it.`
      },

      payloadSections () {
        const command = this.payloadCommand
        if (!command) {
          return []
        }

        return [
          { label: 'Payload', value: command.payload },
          { label: 'Device payload', value: command.devicePayload },
        ]
          .filter(section => section.value !== null && section.value !== undefined && section.value !== '')
          .map(section => ({ label: section.label, text: this.prettyPayload(section.value) }))
      },

      detailRows () {
        const detail = this.detail || {}
        return [
          { label: 'Type', value: detail.type },
          { label: 'Payload', value: this.summarize(detail.payload) },
          { label: 'Device payload', value: this.summarize(detail.devicePayload) },
          { label: 'Status', value: detail.status },
          { label: 'Pending', value: detail.isPending ? 'Yes' : 'No' },
          { label: 'Delivery attempts', value: detail.deliveryAttempts || 0 },
          { label: 'Sent by', value: this.orDash(detail.sentBy) },
          { label: 'Queued at', value: this.formatDate(detail.createdAt) },
          { label: 'Dispatched at', value: this.formatDate(detail.dispatchedAt) },
          { label: 'Last delivery attempt', value: this.formatDate(detail.lastDeliveryAttemptAt) },
          { label: 'Acknowledged at', value: this.formatDate(detail.acknowledgedAt) },
          { label: 'Expires at', value: this.formatDate(detail.expiresAt) },
          { label: 'Last delivery error', value: this.orDash(detail.lastDeliveryError) },
          { label: 'Device group', value: this.orDash(detail.deviceGroupId) },
          { label: 'Command batch', value: this.orDash(detail.commandBatchId) },
        ]
      },
    },

    watch: {
      tab () {
        this.selected = []
        this.reload()
      },

      autoRefresh (value) {
        this.stopAutoRefresh()
        if (value) {
          this.refreshTimer = setInterval(() => this.refreshAll(true), 15000)
        }
      },

      '$route.query': {
        deep: true,
        handler () {
          this.readRoute()
          this.reload()
        },
      },
    },

    mounted () {
      if (!this.canAccess) {
        return
      }
      this.readRoute()
      this.loadVocabulary()
      if (this.hasTarget()) {
        this.loadCommands()
      }
    },

    beforeDestroy () {
      this.stopAutoRefresh()
    },

    methods: {
      hasTarget () {
        return !!(this.target.deviceId || this.target.macAddress)
      },

      readRoute () {
        const query = this.$route.query || {}
        const deviceId = query.deviceId ? Number(query.deviceId) : null
        const macAddress = query.mac || query.macAddress || null

        this.target = {
          deviceId: Number.isFinite(deviceId) && deviceId > 0 ? deviceId : null,
          macAddress: macAddress || null,
        }
        this.lookup.deviceId = this.target.deviceId ? String(this.target.deviceId) : ''
        this.lookup.macAddress = this.target.macAddress || ''
      },

      applyLookup () {
        const deviceId = this.lookup.deviceId ? String(this.lookup.deviceId).trim() : ''
        const macAddress = this.lookup.macAddress ? this.lookup.macAddress.trim() : ''

        if (!deviceId && !macAddress) {
          this.$toast.error('Enter a device id or MAC address to track commands.')
          return
        }

        const query = {}
        if (deviceId) query.deviceId = deviceId
        if (macAddress) query.mac = macAddress

        if (JSON.stringify(query) === JSON.stringify(this.$route.query)) {
          this.reload()
          return
        }

        this.$router.replace({ path: '/device-commands', query })
      },

      // The API resolves a device by id or MAC; send the id when we have one so
      // a stale MAC in the URL cannot contradict it.
      targetParams () {
        if (this.target.deviceId) {
          return { deviceId: this.target.deviceId }
        }
        return this.target.macAddress ? { macAddress: this.target.macAddress } : {}
      },

      reload () {
        this.selected = []
        this.page = 1
        this.loadCommands()
      },

      onPageChange (page) {
        if (page === this.page) {
          return
        }
        this.page = page
        this.selected = []
        this.loadCommands()
      },

      onPageSizeChange (pageSize) {
        if (pageSize === this.pageSize) {
          return
        }
        this.pageSize = pageSize
        this.page = 1
        this.selected = []
        this.loadCommands()
      },

      refreshAll (silent = false) {
        this.loadCommands(silent)
      },

      async loadVocabulary () {
        try {
          const response = await DeviceCommandsDataService.getStatuses()
          const data = response.data || {}

          this.statusVocabulary = this.toOptionList(data.statuses || data.status)
          this.stateVocabulary = this.toOptionList(data.states || data.state)
            .map(entry => ({ text: this.titleCase(entry), value: entry }))
          this.typeVocabulary = this.toOptionList(data.types || data.type)
        } catch (error) {
          // Filters fall back to the built-in vocabulary.
          console.error('Failed to load command status vocabulary:', error)
        }
      },

      toOptionList (value) {
        if (!Array.isArray(value)) {
          return []
        }
        return value
          .map(entry => (typeof entry === 'string' ? entry : (entry?.value ?? entry?.name ?? entry?.text)))
          .filter(Boolean)
      },

      titleCase (value) {
        const text = String(value)
        return text.charAt(0).toUpperCase() + text.slice(1)
      },

      async loadCommands (silent = false) {
        if (!this.hasTarget()) {
          return
        }

        if (!silent) {
          this.loading = true
        }

        const params = {
          ...this.targetParams(),
          page: this.page || 1,
          pageSize: this.pageSize > 0 ? this.pageSize : 25,
        }

        if (this.filters.type) {
          params.type = this.filters.type
        }

        try {
          let response

          if (this.tab === 1) {
            response = await DeviceCommandsDataService.getPending(params)
          } else {
            if (this.filters.state) params.state = this.filters.state
            if (this.filters.status && this.filters.status.length) params.status = this.filters.status
            if (this.filters.from) params.from = this.filters.from
            if (this.filters.to) params.to = this.filters.to

            response = await DeviceCommandsDataService.getHistory(params)
          }

          const data = response.data || {}
          const rows = data.commands || data.items || (Array.isArray(data) ? data : [])

          this.commands = rows
          this.totalCount = data.totalCount === undefined ? rows.length : data.totalCount

          if (data.device) {
            this.device = data.device
          }
          if (data.summary) {
            this.summary = data.summary
          }

          // The pending endpoint does not carry the device/summary block.
          if (this.tab === 1 && (!data.device || !data.summary)) {
            this.loadDeviceSummary()
          }

          this.selected = this.selected.filter(selection =>
            rows.some(row => row.commandId === selection.commandId && row.isPending))
        } catch (error) {
          this.commands = []
          this.totalCount = 0
          this.handleError(error, 'Failed to load device commands')
        } finally {
          this.loading = false
        }
      },

      async loadDeviceSummary () {
        try {
          const response = await DeviceCommandsDataService.getHistory({
            ...this.targetParams(),
            state: 'all',
            page: 1,
            pageSize: 1,
          })
          const data = response.data || {}
          if (data.device) this.device = data.device
          if (data.summary) this.summary = data.summary
        } catch (error) {
          console.error('Failed to load device summary:', error)
        }
      },

      openCancelOneDialog (item) {
        this.cancelMode = 'one'
        this.cancelTargets = [item.commandId]
        this.cancelReason = ''
        this.cancelDialog = true
      },

      openCancelSelectedDialog () {
        this.cancelMode = 'many'
        this.cancelTargets = [...this.selectedPendingIds]
        this.cancelReason = ''
        this.cancelDialog = true
      },

      openCancelAllDialog () {
        this.cancelMode = 'all'
        this.cancelTargets = []
        this.cancelReason = ''
        this.cancelDialog = true
      },

      async confirmCancel () {
        this.cancelling = true

        const payload = {
          ...this.targetParams(),
          commandIds: this.cancelMode === 'all' ? [] : this.cancelTargets,
        }

        if (this.cancelReason) {
          payload.reason = this.cancelReason
        }
        if (this.cancelMode === 'all' && this.filters.type) {
          payload.type = this.filters.type
        }

        try {
          let response

          if (this.cancelMode === 'one') {
            response = await DeviceCommandsDataService.cancel(
              this.cancelTargets[0],
              this.cancelReason ? { reason: this.cancelReason } : {},
            )
          } else {
            response = await DeviceCommandsDataService.cancelMany(payload)
          }

          this.reportCancelResult(response.data)
          this.cancelDialog = false
          this.detailDialog = false
          this.selected = []
          this.loadCommands()
        } catch (error) {
          this.handleError(error, 'Failed to cancel command(s)')
        } finally {
          this.cancelling = false
        }
      },

      reportCancelResult (data) {
        const result = data || {}
        const cancelled = result.cancelled || []
        const notFound = result.notFound || []
        const alreadyCompleted = result.alreadyCompleted || []

        if (cancelled.length) {
          this.$toast.success(`Cancelled ${cancelled.length} command(s).`)
        } else if (!notFound.length && !alreadyCompleted.length) {
          this.$toast.success('Command cancelled.')
        }

        if (alreadyCompleted.length) {
          this.$toast.info(`${alreadyCompleted.length} command(s) had already completed and were left untouched.`)
        }
        if (notFound.length) {
          this.$toast.error(`${notFound.length} command(s) were not found.`)
        }
      },

      async openDetail (item) {
        this.detail = item
        this.detailDialog = true

        try {
          const response = await DeviceCommandsDataService.get(item.commandId)
          if (response.data) {
            this.detail = response.data
          }
        } catch (error) {
          // The row we already have stays on screen.
          console.error('Failed to load command detail:', error)
        }
      },

      resetFilters () {
        this.filters = {
          state: 'all',
          status: [],
          type: null,
          from: null,
          to: null,
        }
        this.reload()
      },

      onFromPicked () {
        this.fromMenu = false
        this.reload()
      },

      onToPicked () {
        this.toMenu = false
        this.reload()
      },

      clearDate (key) {
        this.filters[key] = null
        this.reload()
      },

      stopAutoRefresh () {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer)
          this.refreshTimer = null
        }
      },

      statusColor (status) {
        const colors = {
          Queued: 'blue-grey',
          Dispatched: 'info',
          Succeeded: 'success',
          Acknowledged: 'success',
          Received: 'success',
          Delivered: 'info',
          Failed: 'error',
          Expired: 'orange darken-2',
          Cancelled: 'grey darken-1',
        }
        return colors[status] || 'grey'
      },

      attemptsColor (attempts) {
        const count = Number(attempts) || 0
        if (count > 20) return 'error'
        if (count > 3) return 'warning'
        return 'success'
      },

      isExpired (item) {
        return !!item.expiresAt && this.$format.isPast(item.expiresAt) && !item.acknowledgedAt
      },

      formatDate (date) {
        if (!date) return '-'
        return this.$format.dateTime(date)
      },

      hasPayload (item) {
        if (!item) {
          return false
        }
        const has = value => value !== null && value !== undefined && value !== ''
        return has(item.payload) || has(item.devicePayload)
      },

      openPayload (item) {
        this.payloadCommand = item
        this.payloadDialog = true
      },

      // Command payloads are JSON strings; indent them so the dialog is readable.
      prettyPayload (value) {
        if (typeof value !== 'string') {
          return JSON.stringify(value, null, 2)
        }

        try {
          return JSON.stringify(JSON.parse(value), null, 2)
        } catch (error) {
          return value
        }
      },

      summarize (value) {
        if (value === null || value === undefined || value === '') return '-'
        const text = String(value)
        return text.length > 90 ? `${text.slice(0, 90)}...` : text
      },

      orDash (value) {
        if (value === null || value === undefined || value === '') return '-'
        return value
      },

      handleError (error, fallback) {
        const status = error && error.response ? error.response.status : null
        if (status === 403) {
          this.$toast.error('You are not allowed to manage commands for this device.')
          return
        }
        if (status === 404) {
          this.$toast.error('Device not found.')
          return
        }
        const message = error && error.response && error.response.data
          ? (error.response.data.message || error.response.data.title)
          : null
        this.$toast.error(message || fallback)
      },

      goBack () {
        this.$router.push('/devices')
      },
    },
  }
</script>

<style scoped>
.device-summary__name {
  font-size: 1.05rem;
  font-weight: 600;
}

.device-summary__meta {
  margin-top: 2px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 18px;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
}

.device-summary__meta code {
  font-size: 0.8rem;
  padding: 1px 6px;
  box-shadow: none;
  background: rgba(0, 0, 0, 0.05);
}

.command-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 32px;
}

.command-summary__stats {
  display: flex;
  gap: 28px;
}

.command-summary__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.stat-item {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1;
}

.stat-label {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.6);
}

.timeline {
  padding: 4px 0;
}

.timeline__row {
  display: flex;
  line-height: 1.45;
  font-size: 0.9rem;
  white-space: nowrap;
}

.timeline__k {
  width: 92px;
  flex: none;
  color: rgba(0, 0, 0, 0.55);
}

.timeline__v {
  font-family: monospace;
}

.payload {
  margin-left: -4px;
}

.payload-dialog__label {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.payload-dialog__body {
  margin: 0;
  padding: 12px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-all;
}

.command-link {
  font-weight: 600;
}

.detail-key {
  width: 190px;
  color: rgba(0, 0, 0, 0.6);
}

/* Lift the table and its controls to the 16px of the lookup inputs. */
.device-commands ::v-deep .v-data-table td,
.device-commands ::v-deep .v-data-table .v-data-footer,
.device-commands ::v-deep .v-data-table .v-select__selection {
  font-size: 1rem;
}

.device-commands ::v-deep .v-data-table th {
  font-size: 0.95rem;
}

.device-commands ::v-deep .v-data-table .v-chip--x-small {
  font-size: 0.95rem;
  height: 28px;
  padding: 0 12px;
}

.device-commands ::v-deep .v-chip--x-small .v-icon {
  font-size: 16px;
}

.device-commands ::v-deep .v-tab {
  font-size: 0.875rem;
}

.command-summary ::v-deep .v-chip {
  font-size: 0.95rem;
}

.payload ::v-deep .v-btn__content {
  font-size: 0.75rem;
}

.device-commands ::v-deep .caption {
  font-size: 0.85rem !important;
}
</style>
